import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import AnnotatedImage from "@/components/AnnotatedImage";

/**
 * Before / After walk-through: renders two `AnnotatedImage` charts inside a
 * horizontally swipable slider. Each slide carries its own 3-highlight
 * roadmap so the "setup at planning time" and "how it played out" reads
 * as one continuous story.
 *
 * Auto-advances every 6s, pauses on hover, and STOPS permanently once the
 * user interacts (click a tab, arrow, or dot) — respects the visitor's
 * intent instead of fighting them.
 *
 * Props:
 *   before / after: { src, alt, caption, spots: [{x,y,lx,ly,title,body}] }
 *   autoAdvanceMs: number (default 6000) — pass 0 to disable rotation.
 */
export default function BeforeAfterAnnotated({ before, after, autoAdvanceMs = 6000 }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const userLocked = useRef(false); // permanent stop once the visitor clicks
  const slides = [
    { ...before, kicker: t("annotated.beforeKicker") },
    { ...after,  kicker: t("annotated.afterKicker") },
  ];
  const current = slides[idx];

  const go = (dir) => setIdx((i) => (i + dir + slides.length) % slides.length);
  const goTo = (i) => setIdx(i);
  const lockAndAct = (fn) => () => { userLocked.current = true; fn(); };

  useEffect(() => {
    if (!autoAdvanceMs || userLocked.current || paused) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), autoAdvanceMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvanceMs, paused]);

  return (
    <div
      data-testid="residency-before-after"
      className="space-y-4"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Tab strip */}
      <div className="flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={lockAndAct(() => goTo(i))}
            data-testid={`residency-slide-${i === 0 ? "before" : "after"}`}
            aria-pressed={i === idx}
            className={`text-[10px] uppercase tracking-[0.24em] font-bold px-3 py-1.5 rounded-full border transition-colors ${
              i === idx
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-800"
            }`}
          >
            {s.kicker}
          </button>
        ))}
        <div className="ml-auto hidden sm:flex items-center gap-1">
          <button
            type="button"
            onClick={lockAndAct(() => go(-1))}
            aria-label="Previous chart"
            data-testid="residency-slide-prev"
            className="w-8 h-8 rounded-full border border-slate-200 hover:border-slate-400 flex items-center justify-center text-slate-500 hover:text-slate-900"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="text-[10px] font-mono text-slate-400 tabular-nums">
            {idx + 1} / {slides.length}
          </div>
          <button
            type="button"
            onClick={lockAndAct(() => go(1))}
            aria-label="Next chart"
            data-testid="residency-slide-next"
            className="w-8 h-8 rounded-full border border-slate-200 hover:border-slate-400 flex items-center justify-center text-slate-500 hover:text-slate-900"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Slide */}
      <div className="relative">
        <AnnotatedImage
          src={current.src}
          alt={current.alt}
          spots={current.spots}
          masks={current.masks || []}
          caption={current.caption}
        />
      </div>

      {/* Mobile pager */}
      <div className="sm:hidden flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={lockAndAct(() => go(-1))}
          aria-label="Previous chart"
          className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-[11px] font-mono text-slate-500 tabular-nums">
          {idx + 1} / {slides.length}
        </div>
        <button
          type="button"
          onClick={lockAndAct(() => go(1))}
          aria-label="Next chart"
          className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
